import { useDisclosure } from "../../utils/hooks/useDisclosure";
import { useState } from "react";
import { callAPI } from "../../utils/api";
import { useErrorToast } from "../../utils/showToast";
import { useLogout, useUser } from "../../controllers/UserController";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { OTP_LABEL, PASSWORD_LABEL } from "../../utils/constants/i18n-strings";
import {
  Button,
  Callout,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalTrigger,
  Text,
  VStack,
} from "@wootingkb/component-library";
import { ReplaceableKey } from "../../lib/helper/stringReplacer";
import RegionawareLink from "../Common/RegionawareLink";
import { useSignin } from "../../utils/hooks/useSignIn";

const INVALID_SESSION_ERROR = msg({
  id: "User_delete_account_invalid_session",
  message: "Invalid session. Please try logging in again.",
});

const UNKNOWN_ERROR = msg({
  id: "User_delete_account_unknown_error",
  message: "An unknown error occurred.",
});

const SEND_OTP_BUTTON = msg({
  id: "Auth_send_otp_button",
  message: "Send Code",
});

export default function DeleteAccount() {
  const [fullDelete, setFullDelete] = useState(false);
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    isOpen: confirmDeleteDialogOpen,
    onOpen: onConfirmDeleteDialogOpen,
    onClose: onConfirmDeleteDialogClose,
    onOpenChange: onConfirmDeleteDialogOpenChange,
  } = useDisclosure();

  const showErrorToast = useErrorToast();
  const { mutateAsync: logout } = useLogout();
  const { data: user } = useUser();
  const { _: i18n } = useLingui();
  const { sendOTP } = useSignin(true);
  const [isLoading, setIsLoading] = useState(false);

  // A user "only" has a phone number when they registered through phone OTP:
  // their email is a generated placeholder (@temp.invalid) and a phone number
  // is on file. These users verify deletion with an OTP instead of a password
  // and cannot request a full data erasure.
  const isPhoneOnly =
    !!user && user.email.endsWith("@temp.invalid") && !!user.phoneNumber;

  async function handleSendOtp() {
    if (!user?.phoneNumber) return;
    setOtpSent(true);
    const success = await sendOTP(user.phoneNumber);
    if (!success) setOtpSent(false);
  }

  async function deleteAccount() {
    try {
      if (!user) {
        throw new Error(i18n(INVALID_SESSION_ERROR));
      }

      setIsLoading(true);
      const args = isPhoneOnly
        ? {
            type: "phone",
            phoneNumber: user.phoneNumber,
            otpCode,
            fullDelete: false,
          }
        : { type: "email", email: user.email, password, fullDelete };
      await callAPI(["user"], {
        method: "DELETE",
        args,
      }).then(async () => {
        setIsLoading(false);
        await logout();
      });
    } catch (error) {
      if (error instanceof Error) {
        showErrorToast(error.message);
      } else {
        showErrorToast(i18n(UNKNOWN_ERROR));
      }
    }
  }

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger asChild>
        <Button
          variant="muted"
          size="sm"
          onClick={() => setOpen(true)}
          className="bg-container-error-primary text-foreground-error-secondary-inverted enabled:hover:bg-container-error-primary/90 enabled:active:bg-container-error-primary/75"
        >
          <Trans id="User_delete_account_link">Delete your account</Trans>
        </Button>
      </ModalTrigger>
      <ModalContent
        size="xl"
        className="max-h-full overflow-y-auto md:max-h-[90vh]"
      >
        <ModalBody
          title={
            <Trans id="User_delete_account_dialog_title">Delete account</Trans>
          }
          description={
            isPhoneOnly ? (
              <Trans id="User_delete_account_intro_phone_only">
                You can delete your Wooting Hub account below.
              </Trans>
            ) : (
              <Trans id="User_delete_account_intro">
                You can either delete only your Wooting Hub account, or request
                a full data erasure.
              </Trans>
            )
          }
        >
          <VStack>
            <Text size="sm" weight="semibold">
              <Trans id="User_delete_account_option_title">
                Delete account
              </Trans>
            </Text>
            <Text>
              <Trans id="User_delete_account_option_description">
                Removes your Wooting Hub account. Your personal data remains
                stored according to our Privacy Policy.
              </Trans>
            </Text>
          </VStack>
          {!isPhoneOnly && (
            <>
              <VStack>
                <Text size="sm" weight="semibold">
                  <Trans id="User_erase_all_data_option_title">
                    Erase all data
                  </Trans>
                </Text>
                <Text>
                  <Trans id="User_erase_all_data_option_description">
                    Requests the deletion of all personal data linked to your
                    email address, including any rewards you may be eligible
                    for. This is not reversible, so please consider downloading
                    your data package and any invoices you may wish to keep
                    first.
                  </Trans>
                </Text>
              </VStack>
              <Callout
                status="info"
                heading={
                  <Trans id="User_delete_account_note_title">Note</Trans>
                }
                description={
                  <>
                    <Trans id="User_delete_account_note_body">
                      If you choose to erase all data, some information may
                      still be retained where legally required (for example, for
                      bookkeeping or warranty fulfillment).
                    </Trans>
                    <br />
                    <br />
                    <Trans id="User_delete_account_privacy_policy">
                      Learn more in our{" "}
                      <RegionawareLink
                        href={`${ReplaceableKey.WootingStoreURL}/policies/privacy-policy`}
                        target="_blank"
                        variant="underline"
                        external
                        className="text-sm text-foreground-accent-primary"
                      >
                        Privacy Policy
                      </RegionawareLink>
                      .
                    </Trans>
                  </>
                }
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="secondary"
            size="full"
            onClick={() => setOpen(false)}
          >
            <Trans id="Common_cancel">Cancel</Trans>
          </Button>
          <Flex className="flex-row gap-2">
            <Button
              variant="secondary"
              size="full"
              onClick={() => {
                setFullDelete(false);
                onConfirmDeleteDialogOpen();
              }}
              className="bg-container-error-primary text-foreground-error-secondary-inverted enabled:hover:bg-container-error-primary/90 enabled:active:bg-container-error-primary/75"
            >
              <Trans id="User_delete_hub_account_button">
                Delete Hub Account
              </Trans>
            </Button>
            {!isPhoneOnly && (
              <Button
                variant="secondary"
                size="full"
                onClick={() => {
                  setFullDelete(true);
                  onConfirmDeleteDialogOpen();
                }}
                className="bg-container-error-primary text-foreground-error-secondary-inverted enabled:hover:bg-container-error-primary/90 enabled:active:bg-container-error-primary/75"
              >
                <Trans id="User_erase_all_data_button">Erase All Data</Trans>
              </Button>
            )}
          </Flex>
        </ModalFooter>
        <Modal
          open={confirmDeleteDialogOpen}
          onOpenChange={onConfirmDeleteDialogOpenChange}
        >
          <ModalContent className="max-h-full overflow-y-auto md:max-h-[90vh]">
            <ModalBody
              title={
                <Trans id="User_confirm_account_deletion_title">
                  Confirm Account Deletion
                </Trans>
              }
            >
              {isPhoneOnly ? (
                <>
                  <Text>
                    <Trans id="User_confirm_account_deletion_body_otp">
                      Please enter the code sent to your phone to confirm
                      account deletion.
                    </Trans>
                  </Text>
                  <HStack className="gap-4">
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder={i18n(OTP_LABEL)}
                      autoComplete="one-time-code"
                      isDisabled={!otpSent}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-12 min-h-12"
                      onClick={handleSendOtp}
                      isDisabled={otpSent}
                    >
                      {i18n(SEND_OTP_BUTTON)}
                    </Button>
                  </HStack>
                </>
              ) : (
                <>
                  <Text>
                    <Trans id="User_confirm_account_deletion_body">
                      Please enter your password to confirm account deletion.
                    </Trans>
                  </Text>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={i18n(PASSWORD_LABEL)}
                    autoComplete="current-password"
                  />
                </>
              )}
            </ModalBody>
            <ModalFooter className="flex-row">
              <Button
                variant="secondary"
                size="full"
                onClick={onConfirmDeleteDialogClose}
              >
                <Trans id="Common_cancel">Cancel</Trans>
              </Button>
              <Button
                variant="secondary"
                size="full"
                onClick={() => deleteAccount()}
                isLoading={isLoading}
                disabled={isLoading}
                className="bg-container-error-primary text-foreground-error-secondary-inverted enabled:hover:bg-container-error-primary/90 enabled:active:bg-container-error-primary/75"
              >
                <Trans id="User_confirm_deletion_button">
                  Confirm Deletion
                </Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </ModalContent>
    </Modal>
  );
}
